function getComponent(components, type, useShortName = false) {
  const c = components.find((x) => Array.isArray(x.types) && x.types.includes(type));
  if (!c) return '';
  return useShortName ? (c.short_name || '') : (c.long_name || '');
}

/** Lowercase, trim, single spaces — for `city` / `area` stored in DB. */
function normalizeCityToken(s) {
  return String(s || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[–—-]/g, ' ');
}

/**
 * Pick `city` from Google `address_components` in a fixed order (first non-empty wins).
 * Same idea as a mailing label: locality (Markham, Toronto) before neighbourhood (Scarborough).
 * `administrative_area_level_2` is only used if nothing earlier is present (e.g. some rural rows).
 */
function cityFromGoogleComponents(locality, admin3, subloc1, subloc, neighborhood, admin2) {
  const ordered = [locality, admin3, subloc1, subloc, neighborhood]
    .map((s) => (s || '').trim())
    .filter(Boolean);
  const raw = ordered[0] || (admin2 || '').trim();
  return normalizeCityToken(raw);
}

/**
 * Map Google Places PlaceResult → our address payload fields (before API).
 */
export function parseGooglePlaceToOurAddress(place) {
  const components = place.address_components || [];

  const streetNumber = getComponent(components, 'street_number');
  const route = getComponent(components, 'route');
  const line1 = [streetNumber, route].filter(Boolean).join(' ').trim();

  const subpremise = getComponent(components, 'subpremise');
  const floor = getComponent(components, 'floor');
  const line2 = [subpremise, floor].filter(Boolean).join(', ').trim();

  const subloc1 = getComponent(components, 'sublocality_level_1');
  const subloc = getComponent(components, 'sublocality');
  const neighborhood = getComponent(components, 'neighborhood');
  const area = (subloc1 || subloc || neighborhood || '').trim();

  const postalLong = getComponent(components, 'postal_code');
  const postalShort = getComponent(components, 'postal_code', true);
  const rawPostal = postalShort || postalLong;
  const pincode = String(rawPostal || '')
    .replace(/\s+/g, '')
    .toUpperCase();

  const locality = getComponent(components, 'locality');
  const admin2 = getComponent(components, 'administrative_area_level_2');
  const admin3 = getComponent(components, 'administrative_area_level_3');

  const city = cityFromGoogleComponents(locality, admin3, subloc1, subloc, neighborhood, admin2);

  const loc = place.geometry?.location;
  const latitude = typeof loc?.lat === 'function' ? loc.lat() : Number(loc?.lat);
  const longitude = typeof loc?.lng === 'function' ? loc.lng() : Number(loc?.lng);

  return {
    line1,
    line2,
    area: normalizeCityToken(area) || '',
    city,
    pincode,
    latitude: Number.isFinite(latitude) ? latitude : NaN,
    longitude: Number.isFinite(longitude) ? longitude : NaN,
    formattedAddress: place.formatted_address || '',
  };
}
